// The operations registry — ONE implementation, mounted by both surfaces that show it:
// the per-device popup (clicker-operations-modal.ts) and the team-wide Dashboards tab
// (dashboards/operations-tab.ts). Same toolbar, same filters, same columns, same paging;
// the two differ only in where the rows come from and whether the cross-farm columns
// (Farm filter + Device) are rendered.
//
// Keeping this in one place is deliberate: a near-copy for the team view would have
// drifted the moment either side gained a filter or a column.
import { esc, showModalErr } from '../ui/modal';
import { t } from '../ui/lang';
import { showOperationCommentModal } from './clicker-operation-comment-modal';
import { showOperationScreenshotModal } from './clicker-operation-screenshot-modal';
import { commentSvg, screenshotSvg, refreshIconSvg, downloadSvg, chevronLeftSvg, chevronRightSvg, flowRunSvg, } from '../stream/stream-controls-shared';
import { can } from '../core/access';
import { appState } from '../core/state';
import { routeToHash } from '../core/router';
import { PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE, loadPageSize, savePageSize, pageSizeOptionsHtml, rangeBoundary, fmtTime, dash, pill, } from './clicker-table-shared';
import { clickerAppLabel } from './clicker-apps-shared';
import { buildXlsx, downloadBlob } from './xlsx';
/** Rows one Excel export will pull at most — a filtered week, not the whole retention. */
const EXPORT_MAX_ROWS = 10000;
const EXPORT_PAGE_SIZE = 200;
/** Plain-text twin of a cell that is markup in the table (no pills, no buttons). */
function plainApp(row, apps) {
    const app = (row.app_name || '').trim();
    const rec = apps.get(app);
    const label = rec ? clickerAppLabel(rec) : app;
    const flow = (row.flow_name || '').trim();
    return flow ? `${label}/${flow}` : label;
}
/** Amount/balance as a NUMBER when it is the clicker's canonical shape, so the sheet can
 *  sum a column; anything else (an app's own punctuation) goes across as text. */
function numericCell(v) {
    const s = (v || '').trim();
    return CANONICAL_AMOUNT.test(s) ? Number(s) : s;
}
function syncText(v) {
    if (v === true)
        return t('clickertab.sync_synced');
    if (v === false)
        return t('clickertab.sync_failed');
    return '';
}
/**
 * Excel export of the registry AS FILTERED — not just the page on screen: the same query
 * the table ran, paged through server-side up to EXPORT_MAX_ROWS. The columns mirror the
 * table's, with the Info cell flattened to the comment text (a screenshot has no cell) and
 * the buttons dropped.
 */
async function exportXlsx(params, opts, team, apps) {
    const rows = [];
    for (let page = 1; rows.length < EXPORT_MAX_ROWS; page++) {
        const q = new URLSearchParams(params);
        q.set('page', String(page));
        q.set('pageSize', String(EXPORT_PAGE_SIZE));
        const res = await fetch(opts.endpoint(q));
        if (!res.ok)
            throw new Error('export failed');
        const data = await res.json();
        rows.push(...data.rows);
        if (data.rows.length < EXPORT_PAGE_SIZE || rows.length >= data.total)
            break;
    }
    const withTeam = showTeamPrefix(team);
    const headers = [
        t('clickertab.row_no'), t('clickertab.time'),
        ...(team ? [t('clickertab.device')] : []),
        t('clickertab.operator'), t('clickertab.app'), t('clickertab.type'), t('clickertab.amount'),
        t('clickertab.account_from'), t('clickertab.account_to'), t('clickertab.platform_operation_id'),
        t('common.status'), t('clickertab.person_from'), t('clickertab.person_to'),
        t('clickertab.operation_time'), t('clickertab.balance'), t('clickertab.comment'),
        t('clickertab.platform'), t('clickertab.telegram'),
    ];
    const cells = rows.map((r, i) => [
        i + 1, fmtTime(r.created_at),
        ...(team ? [deviceLabel(r)] : []),
        operatorText(r, withTeam), plainApp(r, apps), r.task_type || '', numericCell(r.amount),
        r.account_from || '', r.account_to || '',
        r.task_type === 'deposit' ? '' : (r.tx_id || ''),
        r.status || '', r.person_from || '', r.person_to || '',
        (r.operation_at || '').trim(), numericCell(r.balance), r.comment || '',
        syncText(r.platform_synced), syncText(r.tg_synced),
    ]);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadBlob(buildXlsx(t('clickertab.ops_title'), headers, cells), `operations-${stamp}.xlsx`);
}
/**
 * How one option reads: "[team/]login · role".
 *
 * The team is prefixed only when the caller reads more than one — which, now that the
 * options are restricted to their own team, means only a superadmin. The role is spelled
 * with the same words the Users page uses; a login that no longer resolves to a user
 * carries neither and shows as itself.
 */
function operatorLabel(o, withTeam) {
    const roleKey = `users.role_${o.role}`;
    const role = o.role ? t(roleKey) : '';
    const name = withTeam && o.team ? `${o.team}/${o.login}` : o.login;
    return role && role !== roleKey ? `${name} · ${role}` : name;
}
/**
 * The User filter's options: everyone on the caller's own team who has a run in view. Any
 * role, not just `operator` — every role can start a clicker, and the column names
 * whoever did.
 *
 * Fetched here rather than through an option like `loadApps`/`loadFarms`, because unlike
 * those it is the SAME endpoint for both surfaces — the per-device popup offers the team's
 * logins too, since the device it shows belongs to that team either way.
 */
async function fetchOperators() {
    const res = await fetch('/api/operations/operators');
    if (!res.ok)
        return [];
    const data = await res.json();
    return Array.isArray(data.operators) ? data.operators : [];
}
/** Sync-status cell: ✓ synced / ✗ failed / — when not applicable (e.g. TG for deposits). */
function syncPill(v) {
    if (v === true)
        return pill(t('clickertab.sync_synced'), 'ok');
    if (v === false)
        return pill(t('clickertab.sync_failed'), 'fail');
    return pill('—', 'na');
}
/** Operation-status pill, colour-coded like the sync cells: success=green, cancel=amber,
 *  decline=red, anything else neutral. Shows the raw status text either way. */
function statusPill(status) {
    const s = (status || '').trim();
    if (!s)
        return pill('—', 'na');
    const k = s.toLowerCase();
    if (k === 'success')
        return pill(s, 'ok');
    if (k === 'cancel')
        return pill(s, 'warn');
    if (k === 'decline' || k === 'declined' || k === 'error' || k === 'failed' || k === 'fail')
        return pill(s, 'fail');
    return pill(s, 'muted');
}
/**
 * The app's own operation timestamp, shown verbatim. The clicker normalizes it to
 * "YYYY-MM-DD HH:MM:SS" but carries NO timezone (and leaves the raw string when a
 * locale-specific date couldn't be parsed), so it must not go through Date() the way
 * `created_at` does — that would re-interpret it in the viewer's zone and shift it.
 */
function fmtOperationAt(v) {
    return dash((v || '').trim());
}
/**
 * Info cell — the two things an operation can leave behind for a human to read: the
 * comment (an app error, a blocklist reason) and the screen the clicker photographed on
 * its way out of a failure. Both are far too big for a cell in a table this wide, so the
 * cell only advertises what exists; a row with neither shows a dash.
 *
 * Only non-successes carry a picture (flow_runner._best_effort_screenshot), and the
 * retention sweep takes them, so most rows have at most the comment.
 */
function infoCell(row) {
    const btns = [];
    if ((row.comment || '').trim()) {
        btns.push(`<button type="button" class="tbl-btn ops-comment-btn" data-id="${row.id}" title="${t('clickertab.show_comment')}">${commentSvg}</button>`);
    }
    if ((row.screenshot || '').trim()) {
        btns.push(`<button type="button" class="tbl-btn ops-shot-btn" data-id="${row.id}" title="${t('clickertab.show_screenshot')}">${screenshotSvg}</button>`);
    }
    return btns.length ? `<span class="ops-info-cell">${btns.join('')}</span>` : '<span class="settings-hint">—</span>';
}
/**
 * Trajectory cell — opens the run this operation recorded ON the flow that produced it,
 * with the steps it walked marked and the one it broke on called out.
 *
 * A NEW TAB, deliberately: the registry is a window with filters, a page and a scroll
 * position someone got to on purpose, and the flow view is where you go to read one row of
 * it. Rows with no stored run (an app on its built-in Python flow, an operation from before
 * the clicker recorded traces) show a dash rather than a button that would 404.
 */
function traceCell(row) {
    if (!row.has_trace)
        return '<span class="settings-hint">—</span>';
    return `<button type="button" class="tbl-btn ops-trace-btn" data-id="${row.id}" title="${t('clickertab.show_flow_run')}">${flowRunSvg}</button>`;
}
/**
 * App cell — the wallet with its country flag (as everywhere else the app is named), and
 * after a slash the panel flow the device was LAUNCHED with when there was one:
 * `AstroPay/Retiros v2`. An auto run names the auto document, not the deposit/payout flow
 * it composes (flow_runner's _report_flow_name) — which of the two ran a given row is the
 * Type column's job. An app running its built-in Python flow shows the app alone, so the
 * slash is itself the answer to "was this row produced by a flow, and by which one". The
 * App filter still matches the app name, not this label. `apps` is the catalog by name; a
 * row whose app is not in it (removed from the catalog) simply gets no flag.
 */
function appCell(row, apps) {
    const app = (row.app_name || '').trim();
    const flow = (row.flow_name || '').trim();
    if (!app && !flow)
        return '<span class="settings-hint">—</span>';
    const rec = apps.get(app);
    const appLabel = rec ? clickerAppLabel(rec) : app;
    if (!flow)
        return esc(appLabel);
    const label = `<span class="ops-flow">/${esc(flow)}</span>`;
    return app ? `${esc(appLabel)}${label}` : label;
}
/**
 * Digit grouping for the Amount column: "50000.00" → "50 000.00".
 *
 * Applied ONLY to the clicker's own canonical shape — major units, dot decimal, at most
 * two decimal places (op_registry / format_minors_to_amount_str). Anything else is left
 * exactly as stored, which is what keeps the older European-formatted rows honest:
 * "70.000" means seventy thousand, and its three decimals are precisely how it fails this
 * test instead of being re-punctuated into seventy.
 *
 * The separator is a non-breaking space, so a grouped amount never wraps mid-number.
 */
const CANONICAL_AMOUNT = /^-?\d+(\.\d{1,2})?$/;
function groupAmount(a) {
    if (!CANONICAL_AMOUNT.test(a))
        return a;
    const [int, dec] = a.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+$)/g, '\u00a0');
    return dec === undefined ? grouped : `${grouped}.${dec}`;
}
/**
 * The Amount / Balance cell: the number alone, grouped.
 *
 * No currency code. A farm runs one currency, the clicker reports it only on some rows
 * (deposits leave it empty), and a column where half the values carry "ARS" and half do
 * not reads as two different things rather than one. The value is still stored — it is
 * the column that stops repeating it.
 */
function fmtAmount(amount) {
    const a = amount.trim();
    if (!a)
        return '<span class="settings-hint">—</span>';
    return esc(groupAmount(a));
}
/**
 * Where the operation happened, in the same "farm/device (udid)" shape the clicker puts
 * on its Telegram notifications and admin comments (common device_label), so a line in
 * the chat and a row here name the phone identically. A device with no alias is just
 * "farm/udid" — repeating the udid in brackets would say nothing twice.
 */
export function deviceLabel(row) {
    const farm = (row.farm_name ?? '').trim();
    const udid = (row.udid ?? '').trim();
    const name = (row.device_name ?? '').trim();
    if (!farm && !udid)
        return '';
    const device = name ? `${name} (${udid})` : udid;
    return farm ? `${farm}/${device}` : device;
}
/**
 * Whether the operator is prefixed with its team: the team-wide surface, and only for a
 * superadmin (see UI_ACCESS 'ops.team'). Everyone else reads exactly one team, so naming
 * it on every row would say the same word forty times.
 */
function showTeamPrefix(team) {
    return team && can('ops.team');
}
/** "team/login" for a superadmin, plain login otherwise; '' when the run has no operator. */
function operatorText(row, withTeam) {
    const login = (row.operator || '').trim();
    const teamName = (row.team_name ?? '').trim();
    if (!login)
        return '';
    return withTeam && teamName ? `${teamName}/${login}` : login;
}
/** Operator tag — the login that started the run this operation came out of. */
function operatorCell(row, withTeam) {
    const label = operatorText(row, withTeam);
    return label ? `<span class="ops-operator">${esc(label)}</span>` : '<span class="settings-hint">—</span>';
}
/**
 * Render the registry into `host` and start loading. The host is filled with the toolbar
 * + the scrolling table; the caller owns whatever chrome surrounds it (modal title and
 * actions, or a page tab).
 */
export function mountOperationsRegistry(host, opts) {
    const team = opts.scope === 'team';
    const initialPageSize = loadPageSize(opts.pageSizeKey);
    // Row number + Time + Operator + the 14 shared data columns, plus Device on the
    // team surface.
    // …and the trajectory column, which only a superadmin is offered (see UI_ACCESS).
    const showTrace = can('ops.flowTrace');
    const showTeam = showTeamPrefix(team);
    const colCount = (team ? 18 : 17) + (showTrace ? 1 : 0);
    const teamFilter = showTeam && opts.loadTeams ? `
        <label class="ops-filter">${t('common.team')}
            <select class="settings-input ops-team"><option value="">${t('clickertab.all')}</option></select>
        </label>` : '';
    const farmFilter = team ? `
        <label class="ops-filter">${t('clickertab.farm')}
            <select class="settings-input ops-farm"><option value="">${t('clickertab.all')}</option></select>
        </label>` : '';
    const deviceHead = team ? `<th>${t('clickertab.device')}</th>` : '';
    const traceHead = showTrace ? `<th>${t('clickertab.flow_run')}</th>` : '';
    host.innerHTML = `
        <div class="clicker-ops-toolbar">
            <label class="ops-filter">${t('clickertab.from')}
                <span class="ops-filter__pair">
                    <input type="date" class="settings-input ops-from" />
                    <input type="time" class="settings-input ops-from-time" aria-label="${t('clickertab.from')} ${t('clickertab.time')}" />
                </span>
            </label>
            <label class="ops-filter">${t('clickertab.to')}
                <span class="ops-filter__pair">
                    <input type="date" class="settings-input ops-to" />
                    <input type="time" class="settings-input ops-to-time" aria-label="${t('clickertab.to')} ${t('clickertab.time')}" />
                </span>
            </label>
            ${teamFilter}${farmFilter}
            <label class="ops-filter">${t('clickertab.app')}
                <select class="settings-input ops-app"><option value="">${t('clickertab.all')}</option></select>
            </label>
            <label class="ops-filter">${t('clickertab.type')}
                <select class="settings-input ops-type">
                    <option value="">${t('clickertab.all')}</option>
                    <option value="payout">${t('clickertab.task_payout_all')}</option>
                    <option value="external_payout">${t('clickertab.task_external_payout')}</option>
                    <option value="merchant_payout">${t('clickertab.task_merchant_payout')}</option>
                    <option value="deposit">${t('clickertab.task_deposit')}</option>
                </select>
            </label>
            <label class="ops-filter">${t('common.status')}
                <select class="settings-input ops-status">
                    <option value="">${t('clickertab.all')}</option>
                    <option value="success">${t('clickertab.status_success')}</option>
                    <option value="cancel">${t('clickertab.status_cancel')}</option>
                    <option value="decline">${t('clickertab.status_decline')}</option>
                </select>
            </label>
            <label class="ops-filter">${t('clickertab.operator')}
                <select class="settings-input ops-operator"><option value="">${t('clickertab.all')}</option></select>
            </label>
            <label class="ops-filter">${t('clickertab.operation_id')}
                <input type="search" class="settings-input ops-tx-id" placeholder="${t('clickertab.account_placeholder')}" />
            </label>
            <label class="ops-filter">${t('clickertab.account_any')}
                <input type="search" class="settings-input ops-account" placeholder="${t('clickertab.account_placeholder')}" />
            </label>
            <span class="clicker-ops-spacer"></span>
            <div class="clicker-ops-pager">
                <label class="ops-filter">${t('clickertab.per_page')}
                    <select class="settings-input ops-page-size">
                        ${pageSizeOptionsHtml(initialPageSize)}
                    </select>
                </label>
                <div class="ops-pager-group">
                    <button type="button" class="tbl-btn ops-prev" title="${t('clickertab.prev')}" aria-label="${t('clickertab.prev')}" disabled>${chevronLeftSvg}</button>
                    <span class="clicker-ops-page">—</span>
                    <button type="button" class="tbl-btn ops-next" title="${t('clickertab.next')}" aria-label="${t('clickertab.next')}" disabled>${chevronRightSvg}</button>
                </div>
                <span class="modal__error ops-export-err" style="display:none"></span>
                <button type="button" class="tbl-btn ops-export" title="${t('clickertab.export_excel')}" aria-label="${t('clickertab.export_excel')}">${downloadSvg}</button>
                <button type="button" class="tbl-btn ops-refresh" title="${t('common.refresh')}" aria-label="${t('common.refresh')}">${refreshIconSvg}</button>
            </div>
        </div>
        <div class="clicker-ops-table-wrap">
            <table class="access-matrix clicker-ops-table">
                <thead><tr>
                    <th class="ops-no">${t('clickertab.row_no')}</th>
                    <th>${t('clickertab.time')}</th>${deviceHead}<th>${t('clickertab.operator')}</th>
                    <th>${t('clickertab.app')}</th><th>${t('clickertab.type')}</th><th>${t('clickertab.amount')}</th>
                    <th>${t('clickertab.account_from')}</th><th>${t('clickertab.account_to')}</th><th>${t('clickertab.platform_operation_id')}</th>
                    <th>${t('common.status')}</th>
                    <th>${t('clickertab.person_from')}</th><th>${t('clickertab.person_to')}</th><th>${t('clickertab.operation_time')}</th>
                    <th>${t('clickertab.balance')}</th><th>${t('clickertab.info')}</th>${traceHead}
                    <th>${t('clickertab.platform')}</th><th>${t('clickertab.telegram')}</th>
                </tr></thead>
                <tbody class="clicker-ops-body"></tbody>
            </table>
        </div>
    `;
    const fromEl = host.querySelector('.ops-from');
    const toEl = host.querySelector('.ops-to');
    // Optional time next to each date. Empty = the whole day, exactly as before the boxes
    // existed (see rangeBoundary).
    const fromTimeEl = host.querySelector('.ops-from-time');
    const toTimeEl = host.querySelector('.ops-to-time');
    const teamEl = host.querySelector('.ops-team');
    const farmEl = host.querySelector('.ops-farm');
    const appEl = host.querySelector('.ops-app');
    const typeEl = host.querySelector('.ops-type');
    const statusEl = host.querySelector('.ops-status');
    const operatorEl = host.querySelector('.ops-operator');
    const txEl = host.querySelector('.ops-tx-id');
    const accountEl = host.querySelector('.ops-account');
    const refreshBtn = host.querySelector('.ops-refresh');
    const exportBtn = host.querySelector('.ops-export');
    const exportErrEl = host.querySelector('.ops-export-err');
    const prevBtn = host.querySelector('.ops-prev');
    const nextBtn = host.querySelector('.ops-next');
    const pageEl = host.querySelector('.clicker-ops-page');
    const pageSizeEl = host.querySelector('.ops-page-size');
    const bodyEl = host.querySelector('.clicker-ops-body');
    let page = 1;
    let total = 0;
    let pageSize = initialPageSize;
    // The rows currently in the table — the comment popup reads its text from here
    // rather than from a data-attribute, so no operator-supplied text is ever inlined
    // into markup.
    let shownRows = [];
    // The catalog by name, for the flag in the App column. It arrives in parallel with the
    // first page, so a page rendered before it lands is re-rendered once it does.
    const appCatalog = new Map();
    let lastPage = null;
    // Populate the App filter (best-effort — a failure leaves "All" only).
    void opts.loadApps()
        .then((apps) => {
        for (const a of apps) {
            appCatalog.set(a.name, a);
            const opt = document.createElement('option');
            opt.value = a.name;
            opt.textContent = clickerAppLabel(a);
            appEl.appendChild(opt);
        }
        if (lastPage)
            render(lastPage);
    })
        .catch(() => { });
    // Populate the Operator filter. The list is what the caller's own scope has seen run
    // (team-scoped server-side); their OWN login goes first, right under "All", because
    // "what did I do today" is the question this filter is opened for — even when they
    // have no operations yet and the server therefore never named them.
    void fetchOperators()
        .then((operators) => {
        // Prefix the team only when the caller actually reads more than one — which is
        // to say, only for a superadmin. Inside a single team the prefix would repeat
        // the same word down forty rows and say nothing.
        const multiTeam = new Set(operators.map((o) => o.team)).size > 1;
        const label = (o) => operatorLabel(o, multiTeam);
        const me = appState.currentUserLogin;
        const rest = operators.filter((o) => o.login !== me)
            .sort((a, b) => label(a).localeCompare(label(b)));
        // Not in the list = this user has never started a clicker in view. Offer them
        // anyway (it is the option they came for), labelled from what the session
        // already knows about themselves.
        const mine = operators.find((o) => o.login === me)
            ?? (me ? {
                login: me,
                team: appState.currentUserTeamName ?? '',
                role: appState.currentUserRole,
            } : null);
        for (const o of (mine ? [mine, ...rest] : rest)) {
            const opt = document.createElement('option');
            opt.value = o.login;
            opt.textContent = label(o);
            operatorEl.appendChild(opt);
        }
    })
        .catch(() => { });
    // The Farm filter offers exactly the farms the caller can reach (the server scopes
    // /api/farms by team and, for operators, by their user_farms grants) — narrowed further
    // to the team picked in the Team filter, so the two filters can never disagree.
    let allFarms = [];
    const renderFarmOptions = () => {
        if (!farmEl)
            return;
        const selected = farmEl.value;
        const teamId = teamEl?.value ? Number(teamEl.value) : null;
        const farms = teamId == null ? allFarms : allFarms.filter((f) => f.teamId === teamId);
        farmEl.innerHTML = `<option value="">${t('clickertab.all')}</option>`;
        for (const f of farms) {
            const opt = document.createElement('option');
            opt.value = String(f.id);
            opt.textContent = f.name;
            farmEl.appendChild(opt);
        }
        farmEl.value = farms.some((f) => String(f.id) === selected) ? selected : '';
    };
    if (farmEl && opts.loadFarms) {
        void opts.loadFarms()
            .then((farms) => { allFarms = farms; renderFarmOptions(); })
            .catch(() => { });
    }
    if (teamEl && opts.loadTeams) {
        void opts.loadTeams()
            .then((teams) => {
            for (const tm of teams) {
                const opt = document.createElement('option');
                opt.value = String(tm.id);
                opt.textContent = tm.name;
                teamEl.appendChild(opt);
            }
        })
            .catch(() => { });
    }
    const setBody = (html) => { bodyEl.innerHTML = html; };
    const centered = (cls, text) => `<tr><td colspan="${colCount}" class="${cls}" style="text-align:center${cls === 'modal__error' ? ';display:table-cell' : ''}">${text}</td></tr>`;
    const render = (data) => {
        total = data.total;
        shownRows = data.rows;
        lastPage = data;
        if (data.rows.length === 0) {
            setBody(centered('settings-hint', t('clickertab.no_operations')));
        }
        else {
            // The number is the row's position in the whole filtered result set, not on
            // the page: an operator quoting "row 214" means the same row after paging.
            const firstNo = (data.page - 1) * data.pageSize;
            setBody(data.rows.map((r, i) => `
                <tr>
                    <td class="ops-no">${firstNo + i + 1}</td>
                    <td>${fmtTime(r.created_at)}</td>
                    ${team ? `<td>${dash(deviceLabel(r))}</td>` : ''}
                    <td>${operatorCell(r, showTeam)}</td>
                    <td>${appCell(r, appCatalog)}</td>
                    <td>${dash(r.task_type)}</td>
                    <td>${fmtAmount(r.amount)}</td>
                    <td>${dash(r.account_from)}</td>
                    <td>${dash(r.account_to)}</td>
                    <td>${r.task_type === 'deposit' ? '<span class="settings-hint">—</span>' : dash(r.tx_id)}</td>
                    <td>${statusPill(r.status)}</td>
                    <td>${dash(r.person_from)}</td>
                    <td>${dash(r.person_to)}</td>
                    <td>${fmtOperationAt(r.operation_at)}</td>
                    <td>${fmtAmount(r.balance)}</td>
                    <td>${infoCell(r)}</td>
                    ${showTrace ? `<td>${traceCell(r)}</td>` : ''}
                    <td>${syncPill(r.platform_synced)}</td>
                    <td>${syncPill(r.tg_synced)}</td>
                </tr>
            `).join(''));
        }
        const pages = Math.max(1, Math.ceil(total / pageSize));
        pageEl.textContent = t(total === 1 ? 'clickertab.ops_pager_one' : 'clickertab.ops_pager_many', { page: data.page, pages, total });
        prevBtn.disabled = data.page <= 1;
        nextBtn.disabled = data.page >= pages;
    };
    // Filters change faster than the requests come back, so every load carries a sequence
    // number and only the newest one is allowed to touch the table — otherwise a slow
    // earlier response lands last and the table shows rows for a filter that is no longer
    // selected.
    let reqSeq = 0;
    /** The filters as query params — shared by the table load and the Excel export. */
    const filterParams = () => {
        const params = new URLSearchParams();
        const fromIso = rangeBoundary(fromEl.value, fromTimeEl.value);
        const toIso = rangeBoundary(toEl.value, toTimeEl.value, true);
        if (fromIso)
            params.set('from', fromIso);
        if (toIso)
            params.set('to', toIso);
        if (teamEl?.value)
            params.set('teamId', teamEl.value);
        if (farmEl?.value)
            params.set('farmId', farmEl.value);
        if (appEl.value)
            params.set('app', appEl.value);
        if (typeEl.value)
            params.set('type', typeEl.value);
        if (statusEl.value)
            params.set('status', statusEl.value);
        if (operatorEl.value)
            params.set('operator', operatorEl.value);
        if (txEl.value.trim())
            params.set('txId', txEl.value.trim());
        if (accountEl.value.trim())
            params.set('account', accountEl.value.trim());
        return params;
    };
    const load = async () => {
        const seq = ++reqSeq;
        setBody(centered('settings-hint', t('common.loading')));
        const params = filterParams();
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
        try {
            const res = await fetch(opts.endpoint(params));
            if (seq !== reqSeq)
                return;
            if (!res.ok) {
                setBody(centered('modal__error', t('clickertab.failed_to_load_operations')));
                prevBtn.disabled = true;
                nextBtn.disabled = true;
                return;
            }
            const data = await res.json();
            if (seq !== reqSeq)
                return;
            render(data);
        }
        catch {
            if (seq !== reqSeq)
                return;
            setBody(centered('modal__error', t('clickertab.network_error_dot')));
        }
    };
    // Any filter change resets to the first page; the pager steps within the result set.
    const reload = () => { page = 1; void load(); };
    fromEl.addEventListener('change', reload);
    toEl.addEventListener('change', reload);
    fromTimeEl.addEventListener('change', reload);
    toTimeEl.addEventListener('change', reload);
    // Picking a team re-narrows the Farm options; a farm outside it drops back to "All".
    teamEl?.addEventListener('change', () => { renderFarmOptions(); reload(); });
    farmEl?.addEventListener('change', reload);
    appEl.addEventListener('change', reload);
    typeEl.addEventListener('change', reload);
    statusEl.addEventListener('change', reload);
    operatorEl.addEventListener('change', reload);
    // The search boxes are typed into, so they reload on a short idle instead of on every
    // keystroke; Enter (and the search box's clear "x", which fires `change`) reloads at once.
    let typingTimer;
    const reloadSoon = () => {
        window.clearTimeout(typingTimer);
        typingTimer = window.setTimeout(reload, 350);
    };
    const reloadNow = () => { window.clearTimeout(typingTimer); reload(); };
    for (const el of [txEl, accountEl]) {
        el.addEventListener('input', reloadSoon);
        el.addEventListener('change', reloadNow);
        el.addEventListener('keydown', (e) => { if (e.key === 'Enter')
            reloadNow(); });
    }
    refreshBtn.addEventListener('click', () => { void load(); }); // reloads the current page + filters
    exportBtn.addEventListener('click', () => {
        exportBtn.disabled = true;
        exportErrEl.style.display = 'none';
        void exportXlsx(filterParams(), opts, team, appCatalog)
            .catch(() => { showModalErr(exportErrEl, t('clickertab.export_failed')); })
            .finally(() => { exportBtn.disabled = false; });
    });
    prevBtn.addEventListener('click', () => { if (page > 1) {
        page--;
        void load();
    } });
    nextBtn.addEventListener('click', () => {
        if (page < Math.ceil(total / pageSize)) {
            page++;
            void load();
        }
    });
    // Changing rows-per-page resets to the first page and is remembered for next time.
    pageSizeEl.addEventListener('change', () => {
        const n = parseInt(pageSizeEl.value, 10);
        pageSize = PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE;
        savePageSize(opts.pageSizeKey, pageSize);
        page = 1;
        void load();
    });
    // Comment buttons are re-rendered on every page/filter change, so the click is
    // delegated from the tbody instead of bound per row.
    bodyEl.addEventListener('click', (e) => {
        const trace = e.target.closest('.ops-trace-btn');
        if (trace) {
            const row = shownRows.find((r) => r.id === Number(trace.dataset['id']));
            // The link names the operation only: which flow, and which redaction of it, is
            // what the stored run resolves to on the server.
            if (row)
                window.open(routeToHash({ page: 'flows', opId: row.id }), '_blank');
            return;
        }
        const shot = e.target.closest('.ops-shot-btn');
        const btn = shot ?? e.target.closest('.ops-comment-btn');
        if (!btn)
            return;
        const row = shownRows.find((r) => r.id === Number(btn.dataset['id']));
        if (!row)
            return;
        const app = [row.app_name, row.flow_name].filter((s) => (s || '').trim()).join('/');
        const meta = [deviceLabel(row), app, row.task_type, row.tx_id].filter(Boolean).join(' · ');
        const popupHost = host.closest('.modal') ?? document.body;
        if (shot)
            showOperationScreenshotModal(popupHost, row.id, meta);
        else
            showOperationCommentModal(popupHost, row.comment, meta);
    });
    void load();
}
